import { getApiUrl } from '@/lib/api-config';

/** A LOCAL person of the company (user_company_relation.local_id), not a global account id. */
export interface ProjectPerson {
  local_id: string;
  name: string;
  email: string | null;
  company_role: 'admin' | 'member' | 'client' | 'vendor';
  /** 'internal' = admin / member, 'external' = client / vendor: decides which table the person belongs to */
  kind: 'internal' | 'external';
}

export interface ProjectAccess {
  project_id: string;
  /** only company admins replace the member list (and add clients / vendors) */
  can_manage: boolean;
  /** admins and members of the project add internal people (never remove anybody) */
  internal_members: ProjectPerson[];
  external_members: ProjectPerson[];
}

export const NO_INTERNAL_MEMBER_MESSAGE = 'A project needs at least one internal member.';

async function call<T = ProjectAccess>(path: string, method: string, body?: unknown): Promise<T> {
  const response = await fetch(getApiUrl(path), {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || result?.success === false) throw new Error(result?.error?.message || result?.message || result?.error || 'The request could not be completed');
  return result.data as T;
}

export const fetchProjectAccess = (projectId: string) => call(`api/projects/${projectId}/access`, 'GET');
/** `next` is the cursor of the following page (null on the last one) */
export interface ProjectPeoplePage { results: ProjectPerson[]; has_more: boolean; next: string | null; limit: number }
/**
 * One page of the company's people (the internal team, then vendors, then clients, each A-Z, unless `kind` narrows it) whose name, company or email has every word of `q`, searched on the server over the whole
 * company. `exclude` is the modal's draft: those people are not offered, and a stored member that was removed from the draft is.
 */
export const searchProjectPeople = (projectId: string, { q = '', kind, cursor, exclude = [] }: { q?: string; kind?: ProjectPerson['kind']; cursor?: string | null; exclude?: string[] }) =>
  call<ProjectPeoplePage>(
    `api/projects/${projectId}/access/search?q=${encodeURIComponent(q)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}${kind ? `&kind=${kind}` : ''}${exclude.length > 0 ? `&exclude=${exclude.join(',')}` : ''}`,
    'GET',
  );
/** Is this vendor / client (a local person of the company) already in the project? Asked right after one is assigned to a document. */
export interface ProjectPersonCheck { project_id: string; person_id: string; company_role: ProjectPerson['company_role']; in_project: boolean; can_manage: boolean }
export const checkProjectPerson = (projectId: string, personId: string) =>
  call<ProjectPersonCheck>(`api/projects/${projectId}/access/check?person_id=${encodeURIComponent(personId)}`, 'GET');
/** Adds ONE vendor / client to the project (company admins only; the server decides). */
export const addProjectMember = (projectId: string, personId: string) =>
  call<{ project_id: string; person_id: string; in_project: boolean; added: boolean }>(`api/projects/${projectId}/access/members/${personId}`, 'POST');
export const saveProjectMembers = (projectId: string, members: string[]) => call(`api/projects/${projectId}/access/members`, 'PUT', { members });
/** Adds people to the project, never removing anybody: admins and members of the project add anybody (the server decides). */
export const addProjectMembers = (projectId: string, members: string[]) =>
  call<{ project_id: string; members: string[]; added: string[] }>(`api/projects/${projectId}/access/members`, 'POST', { members });

/**
 * Saves the project's own fields and then its members as one unit. The fields go first: they are the save that can be refused for many reasons, and an admin who removes
 * themself from the list must not lose the project before the fields are stored. When the members are refused, `undo` puts the fields back, so a failed Save Changes leaves
 * the project as it was. `members` is undefined when the list did not change.
 */
export async function saveProjectAndMembers({ projectId, members, save, undo }: {
  projectId: string;
  members?: string[];
  save: () => Promise<void>;
  undo: () => Promise<void>;
}): Promise<void> {
  await save();
  if (!members) return;
  try {
    await saveProjectMembers(projectId, members);
  } catch (error) {
    try {
      await undo();
    } catch {
      const reason = error instanceof Error ? error.message : 'The members could not be saved';
      throw new Error(`${reason} The project fields were saved and could not be restored: reopen the project to check them.`);
    }
    throw error;
  }
}
